import { motion } from 'framer-motion'
import { WifiOff } from 'lucide-react'
import { cn } from '@/lib/utils'

// Action Timer Arc — wraps avatar only
function ActionTimer({ seconds, maxSeconds }: { seconds: number; maxSeconds: number }) {
  const progress = seconds / maxSeconds
  const radius = 22
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - progress)
  const color = seconds <= 5 ? '#ef4444' : seconds <= 10 ? '#eab308' : '#f59e0b'

  return (
    <svg className="absolute -inset-1.5 w-[calc(100%+12px)] h-[calc(100%+12px)] -rotate-90 z-0" viewBox="0 0 52 52">
      <circle cx="26" cy="26" r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
      <circle
        cx="26" cy="26" r={radius} fill="none" stroke={color}
        strokeWidth="3" strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        className="transition-all duration-1000 linear"
        style={{ filter: `drop-shadow(0 0 4px ${color})` }}
      />
    </svg>
  )
}

// ── Card helpers ───────────────────────────────────────────────
const getSuitColor = (suit: string) => suit === 'hearts' || suit === 'diamonds' ? 'text-red-500' : 'text-slate-800'
const getSuitIcon = (suit: string) => {
  switch (suit) {
    case 'spades': return '♠'
    case 'hearts': return '♥'
    case 'clubs': return '♣'
    case 'diamonds': return '♦'
    default: return ''
  }
}

export interface PlayerData {
  seat: number
  name: string
  avatar: string
  stack: number
  bet: number
  status: 'active' | 'folded' | 'current' | 'all_in' | 'empty'
  country: string
  isDealer: boolean
}

interface PlayerNameplateProps {
  player: PlayerData
  isCompact: boolean
  isDisconnected?: boolean
  isSittingOut?: boolean
  isHero?: boolean
  holeCards?: { rank: string; suit: string }[]
  timerSeconds?: number
  timerMax?: number
  showSitDown?: boolean
  onSelectSeat?: () => void
  handRanking?: string
}

export function PlayerNameplate({
  player,
  isCompact,
  isDisconnected = false,
  isSittingOut = false,
  isHero = false,
  holeCards,
  timerSeconds = 0,
  timerMax = 30,
  showSitDown = false,
  onSelectSeat,
  handRanking,
}: PlayerNameplateProps) {
  if (player.status === 'empty') {
    return (
      <motion.button
        onClick={onSelectSeat}
        disabled={!showSitDown}
        whileHover={showSitDown ? { scale: 1.1 } : {}}
        whileTap={showSitDown ? { scale: 0.95 } : {}}
        className={cn(
          'flex flex-col items-center justify-center gap-0.5 transition-all',
          showSitDown ? 'cursor-pointer' : ''
        )}
      >
        <div className={cn(
          'w-10 h-10 rounded-full border border-dashed flex items-center justify-center',
          showSitDown
            ? 'border-gold bg-gold/20 shadow-[0_0_12px_rgba(212,175,55,0.4)] animate-pulse border-2'
            : 'border-white/[0.12] bg-white/[0.04]'
        )}>
          {showSitDown ? (
            <span className="text-[9px] font-bold text-gold">坐下</span>
          ) : (
            <span className="text-white/20 text-xs">▼</span>
          )}
        </div>
        <span className={cn(
          'text-[8px] font-medium',
          showSitDown ? 'text-gold/80' : 'text-white/20'
        )}>
          {showSitDown ? '入座' : 'Take Seat'}
        </span>
      </motion.button>
    )
  }

  const showFaceDown = !isHero && player.status !== 'folded' && !holeCards
  const showFaceUp = !isHero && player.status !== 'folded' && holeCards

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={cn('relative flex flex-col items-center', player.status === 'folded' && 'opacity-40 grayscale')}
    >
      {/* Hole cards — positioned behind the nameplate, peeking above it */}
      {/* Face-down cards (portrait-sized) */}
      {showFaceDown && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-[1] flex items-center justify-center">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="w-[28px] h-[39px] rounded-[3px] border shadow-md relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 border-blue-700/50"
              style={{
                transformOrigin: 'bottom center',
                rotate: i === 0 ? '-8deg' : '8deg',
                marginRight: i === 0 ? '-6px' : 0,
              }}
            >
              <div className="absolute inset-[2px] rounded-[2px] border border-blue-600/30">
                <div className="absolute inset-0 bg-[repeating-conic-gradient(#1e3a5f_0%_25%,#1e40af_0%_50%)] opacity-30 bg-[length:6px_6px]" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Face-up cards (showdown) — portrait-sized, in front of avatar */}
      {showFaceUp && holeCards && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-[14px] z-[15] flex items-center justify-center">
          {holeCards.map((card, i) => (
            <motion.div
              key={i}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: i * 0.1, duration: 0.3, type: 'spring' }}
              className="relative overflow-hidden w-[32px] h-[44px]"
              style={{
                transformOrigin: 'bottom center',
                rotate: i === 0 ? '-8deg' : '8deg',
                marginRight: i === 0 ? '-8px' : 0,
              }}
            >
              <div className="absolute inset-0 bg-black/30 rounded-md blur-sm translate-y-0.5" />
              <div className="relative w-full h-full bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-[3px] border border-gray-200/80 overflow-hidden shadow-md">
                <div className={cn('absolute top-0 left-0.5 flex flex-col items-center leading-none', getSuitColor(card.suit))}>
                  <span className="font-black font-serif text-sm">{card.rank}</span>
                  <span className="text-[8px] -mt-0.5">{getSuitIcon(card.suit)}</span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-transparent" />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Avatar container */}
      <div className="relative z-10">
        {/* Timer ring */}
        {player.status === 'current' && (
          <ActionTimer seconds={timerSeconds} maxSeconds={timerMax} />
        )}

        {/* Status ring */}
        {player.status === 'current' && (
          <div className="absolute -inset-1.5 rounded-full overflow-hidden z-0">
            <div className="absolute inset-0 bg-conic-gradient from-gold via-transparent to-gold animate-spin-slow" />
            <div className="absolute inset-[2px] rounded-full bg-surface-sunken" />
          </div>
        )}
        {player.status === 'active' && !player.isDealer && (
          <div className="absolute -inset-1 rounded-full border-2 border-gold/50 z-0" />
        )}
        {player.status === 'all_in' && (
          <div className="absolute -inset-1 rounded-full border-2 border-red-500 shadow-[0_0_12px_rgba(239,68,68,0.5)] animate-pulse z-0" />
        )}

        {/* Dealer button */}
        {player.isDealer && (
          <div className={cn(
            'absolute rounded-full bg-white font-bold text-black flex items-center justify-center shadow-lg z-30 border border-gray-200',
            isCompact ? '-bottom-1 -left-1 w-4 h-4 text-[7px]' : '-bottom-1.5 -left-1.5 w-5 h-5 text-[9px]'
          )}>D</div>
        )}

        {/* Avatar circle */}
        <div className={cn(
          'w-10 h-10 text-lg rounded-full bg-surface-elevated border-2 border-white/10 flex items-center justify-center shadow-lg relative z-10 overflow-hidden',
          isDisconnected && 'opacity-60'
        )}>
          <span className="relative z-10">{player.avatar}</span>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20" />
        </div>

        {/* Disconnect indicator */}
        {isDisconnected && (
          <div className="absolute -top-1 -left-1 w-4 h-4 rounded-full bg-red-600 flex items-center justify-center z-30 shadow-md border border-red-500/50">
            <WifiOff className="w-2.5 h-2.5 text-white" />
          </div>
        )}

        {/* Sit-out badge */}
        {isHero && isSittingOut && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="px-1.5 py-0.5 rounded bg-amber-500/90 text-[8px] font-bold text-white shadow-lg">
              暫離
            </div>
          </div>
        )}

        {/* Country flag */}
        <div className={cn(
          'absolute -bottom-0.5 -right-0.5 rounded-full bg-surface-sunken border border-white/10 flex items-center justify-center z-20 shadow-md',
          isCompact ? 'w-4 h-4 text-[7px]' : 'w-5 h-5 text-[9px]',
          isDisconnected && 'opacity-60'
        )}>
          {player.country}
        </div>
      </div>

      {/* Status badge — Fold / All In (competitor-style) */}
      {player.status === 'folded' && (
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-30 px-1.5 py-0.5 rounded bg-slate-600/90 text-[8px] font-bold text-white/90 uppercase tracking-wide shadow-sm">
          Fold
        </div>
      )}
      {player.status === 'all_in' && (
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-30 px-1.5 py-0.5 rounded bg-red-600/90 text-[8px] font-bold text-white uppercase tracking-wide shadow-sm shadow-red-500/30">
          All In
        </div>
      )}

      {/* Nameplate: name + stack */}
      <div className="mt-1 rounded-lg bg-black/60 backdrop-blur-sm text-center px-2 py-0.5 relative z-20 w-[80px]">
        <span className="text-white font-medium truncate block leading-tight text-[10px]">{player.name}</span>
        <span className={cn(
          'font-mono font-bold block leading-tight text-[10px]',
          player.status === 'all_in' ? 'text-red-400' : 'text-gold'
        )}>
          {player.status === 'all_in' ? '$0' : `$${(player.stack / 100).toLocaleString()}`}
        </span>
      </div>

      {/* Hand ranking label at showdown */}
      {handRanking && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-0.5 px-1.5 py-0.5 rounded bg-black/50 backdrop-blur-sm z-20"
        >
          <span className="text-[8px] font-bold text-gold/80 uppercase tracking-wider whitespace-nowrap">{handRanking}</span>
        </motion.div>
      )}
    </motion.div>
  )
}
